/**
 * useGlobalSearch — Hook da pesquisa global do header
 * -----------------------------------------------------------------------------
 * Cobre Contatos, Clientes, Veículos, Negócios e os três tipos de Tarefa
 * (Normal, Retenção e Jurídico) em uma única RPC (`global_search`).
 *
 * Responsabilidades:
 *  - Aplicar debounce no termo digitado (evita consulta a cada tecla).
 *  - Exigir mínimo de 3 caracteres antes de consultar o banco.
 *  - Chamar a RPC única `global_search` (leve, sem COUNT total, limitada por tipo).
 *  - Cachear por termo + escopo no TanStack Query.
 *
 * Performance:
 *  - O escopo selecionado (chips do dropdown) é enviado em `p_types`; blocos não
 *    solicitados nem chegam a ser executados no banco.
 *  - Com um único tipo selecionado o limite sobe de 5 para 10 (mesmo custo por
 *    bloco, porém mais resultados úteis para o usuário).
 *
 * Segurança: nenhum tenant_id é enviado pelo frontend — a RPC resolve o tenant
 * validado do usuário autenticado internamente e aplica as regras de
 * visibilidade por registro (negócios e tarefas).
 *
 * @version 2.0.0
 * RELEASE: 2026-08-22 v2.0.0 - Novos tipos `deal` e `task:normal|retention|legal`,
 *          escopo dinâmico (`types`) e limite adaptativo.
 * RELEASE: 2026-08-09 v1.1.0 - Inclui o tipo `vehicle` (pesquisa por placa/marca/modelo)
 * RELEASE: 2026-08-06 v1.0.0 - Versão inicial
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/** Quantidade mínima de caracteres para disparar a busca */
export const GLOBAL_SEARCH_MIN_CHARS = 3;

/** Tempo de debounce (ms) aplicado ao termo digitado */
const DEBOUNCE_MS = 350;

/** Tipos aceitos pela RPC `global_search` (v1.3.0) */
export type GlobalSearchType =
  | 'contact'
  | 'customer'
  | 'vehicle'
  | 'deal'
  | 'task:normal'
  | 'task:retention'
  | 'task:legal';

/** Todos os tipos suportados, na ordem de exibição do dropdown */
export const GLOBAL_SEARCH_ALL_TYPES: GlobalSearchType[] = [
  'contact',
  'customer',
  'vehicle',
  'deal',
  'task:normal',
  'task:retention',
  'task:legal',
];

/** Resultado normalizado retornado pela RPC `global_search` */
export interface GlobalSearchResult {
  entity_type: GlobalSearchType;
  entity_id: string;
  title: string | null;
  subtitle: string | null;
  avatar_url: string | null;
  extra: Record<string, unknown> | null;
  rank: number;
}

/**
 * Hook principal da pesquisa global.
 * @param term Termo digitado pelo usuário (valor bruto do input)
 * @param enabled Permite desabilitar a busca (ex.: popover fechado)
 * @param types Escopo consultado (chips). Default: todos os tipos.
 */
export function useGlobalSearch(
  term: string,
  enabled = true,
  types: GlobalSearchType[] = GLOBAL_SEARCH_ALL_TYPES,
) {
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // Debounce simples do termo digitado
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const isValidTerm = debouncedTerm.length >= GLOBAL_SEARCH_MIN_CHARS;

  // Chave estável do escopo (ordem não deve gerar cache novo)
  const scopeKey = [...types].sort().join(',');

  const query = useQuery({
    queryKey: ['global-search', debouncedTerm, scopeKey],
    enabled: enabled && isValidTerm && types.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: async (): Promise<GlobalSearchResult[]> => {
      const { data, error } = await supabase.rpc('global_search', {
        p_query: debouncedTerm,
        p_types: types,
        // Escopo único = pode mostrar mais itens sem custo adicional relevante
        p_limit: types.length === 1 ? 10 : 5,
      });

      if (error) {
        logger.error('GlobalSearch', 'Erro na pesquisa global', { error: error.message });
        throw error;
      }

      return (data ?? []) as unknown as GlobalSearchResult[];
    },
  });

  const results = query.data ?? [];
  const byType = (t: GlobalSearchType) => results.filter((r) => r.entity_type === t);

  return {
    /** Termo já debounced (usado para exibir "Ver todos") */
    debouncedTerm,
    /** Indica se o termo atende ao mínimo de caracteres */
    isValidTerm,
    /** Está aguardando o debounce ou a resposta do banco */
    isLoading: enabled && isValidTerm && (query.isLoading || term.trim() !== debouncedTerm),
    isError: query.isError,
    /** Resultados agrupados por tipo */
    contacts: byType('contact'),
    customers: byType('customer'),
    /** v1.1.0 — veículos encontrados (placa/marca/modelo/chassi) */
    vehicles: byType('vehicle'),
    /** v2.0.0 — negócios (título/cliente/contato) */
    deals: byType('deal'),
    /** v2.0.0 — tarefas por tipo */
    tasksNormal: byType('task:normal'),
    tasksRetention: byType('task:retention'),
    tasksLegal: byType('task:legal'),
    total: results.length,
  };
}
